// src/pages/PartnersPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useThemePalette } from '../hooks/useThemePalette';
import { useThemeStore }   from '../stores/useThemeStore';

// ── Shared SVG props ──────────────────────────────────────────────────────────
const SI = { fill: 'none', stroke: 'currentColor', strokeWidth: '1.9', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

// ── Accent purple (mirrors HomePage) ─────────────────────────────────────────
const PURPLE = '#4f3ff5';

// ── Partner data ──────────────────────────────────────────────────────────────
const PARTNERS = [
  {
    id:          'gsus',
    logo:        '/images/Georgia.png',
    name:        'Georgian State University of Sport',
    description: 'Leading sports education institution in Georgia, contributing expertise in sports science and athletic training methodologies to our curriculum development.',
    url:         'https://sportuni.ge/index.php?lang=eng',
    country:     'Georgia',
    countryCode: 'GEO',
    tag:         'University',
  },
  {
    id:          'mpec',
    logo:        '/images/mpec.png',
    name:        'MPEC – My Personal English Coach',
    description: 'Specialized language training provider offering innovative approaches to sports-specific English instruction and digital learning solutions across Italy and beyond.',
    url:         'https://mpec.it/en/courses/english-for-sports/',
    country:     'Italy',
    countryCode: 'ITA',
    tag:         'Language Training',
  },
];

// Ticker: duplicate set for seamless loop
const TICKER_PARTNERS = [...PARTNERS, ...PARTNERS, ...PARTNERS, ...PARTNERS, ...PARTNERS, ...PARTNERS];

// ── Keyframe injection (mirrors HomePage pattern) ─────────────────────────────
(function injectPartnersStyles() {
  if (typeof document === 'undefined') return;
  const id = 'esp-partners-kf';
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @keyframes ptnEnter {
      from { opacity: 0; transform: translateY(24px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes ptnFadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes ptnMeshFloat1 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      33%      { transform: translate(-50px, 65px) scale(1.07); }
      66%      { transform: translate(35px, -45px) scale(0.94); }
    }
    @keyframes ptnMeshFloat2 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      40%      { transform: translate(60px, -58px) scale(1.10); }
      70%      { transform: translate(-30px, 40px) scale(0.93); }
    }
    @keyframes ptnMeshFloat3 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50%      { transform: translate(-62px, -30px) scale(1.06); }
    }
    @keyframes ptnPulseGlow {
      0%, 100% { opacity: 0.45; transform: scale(1);    }
      50%      { opacity: 0.85; transform: scale(1.05); }
    }
    @keyframes ptnPulseGlow2 {
      0%, 100% { opacity: 0.28; transform: scale(1) rotate(0deg);   }
      33%      { opacity: 0.65; transform: scale(1.05) rotate(60deg);  }
      66%      { opacity: 0.40; transform: scale(0.96) rotate(120deg); }
    }
    @keyframes ptnParticleDrift {
      0%   { transform: translate(0, 0) scale(1);   opacity: var(--p-opacity); }
      33%  { transform: translate(var(--px), var(--py)) scale(1.4); opacity: calc(var(--p-opacity) * 1.6); }
      66%  { transform: translate(calc(var(--px) * -0.5), calc(var(--py) * 1.3)) scale(0.8); opacity: calc(var(--p-opacity) * 0.6); }
      100% { transform: translate(0, 0) scale(1);   opacity: var(--p-opacity); }
    }
    @keyframes ptnStatFloat {
      0%, 100% { transform: translateY(0px);  }
      50%      { transform: translateY(-7px); }
    }
    @keyframes ptnStatEnter {
      from { opacity: 0; transform: scale(0.84) translateY(12px); }
      to   { opacity: 1; transform: scale(1)    translateY(0);    }
    }
    @keyframes ptnOrbitRing {
      from { transform: translate(-50%, -50%) rotate(0deg);   }
      to   { transform: translate(-50%, -50%) rotate(360deg); }
    }
    @keyframes ptnOrbitRingRev {
      from { transform: translate(-50%, -50%) rotate(0deg);    }
      to   { transform: translate(-50%, -50%) rotate(-360deg); }
    }
    @keyframes ptnEcPulse1 {
      0%   { stroke-dashoffset:  180; opacity: 0;   }
      6%   { opacity: 0.9; }
      82%  { opacity: 0.9; }
      92%  { stroke-dashoffset: -980; opacity: 0;   }
      100% { stroke-dashoffset: -980; opacity: 0;   }
    }
    @keyframes ptnEcPulse2 {
      0%   { stroke-dashoffset:  140; opacity: 0;   }
      7%   { opacity: 0.7; }
      83%  { opacity: 0.7; }
      93%  { stroke-dashoffset: -920; opacity: 0;   }
      100% { stroke-dashoffset: -920; opacity: 0;   }
    }
    @keyframes ptnEcPulse3 {
      0%   { stroke-dashoffset:  110; opacity: 0;   }
      8%   { opacity: 0.62; }
      84%  { opacity: 0.62; }
      94%  { stroke-dashoffset: -860; opacity: 0;   }
      100% { stroke-dashoffset: -860; opacity: 0;   }
    }
    @keyframes esp-ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
    .esp-ticker-track { animation: esp-ticker 38s linear infinite; }
    .esp-ticker-track:hover { animation-play-state: paused; }
    @keyframes ptnCardReveal {
      from { opacity: 0; transform: translateY(22px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes ptnShimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes ptnBarExpand {
      from { transform: scaleX(0); }
      to   { transform: scaleX(1); }
    }
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;
  document.head.appendChild(s);
})();

// ── Particle field (mirrors HomePage) ────────────────────────────────────────
function ParticleField({ isDark }: { isDark: boolean }) {
  const particles = React.useMemo(() => {
    const rng = (min: number, max: number) => min + Math.random() * (max - min);
    return Array.from({ length: 24 }, (_, i) => ({
      id:      i,
      left:    `${rng(0, 100)}%`,
      top:     `${rng(0, 100)}%`,
      size:    rng(1, 3),
      opacity: rng(0.05, 0.26),
      dur:     `${rng(9, 22)}s`,
      delay:   `${rng(0, 14)}s`,
      px:      `${rng(-28, 28)}px`,
      py:      `${rng(-28, 28)}px`,
    }));
  }, []);

  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position:     'absolute',
            left:         p.left,
            top:          p.top,
            width:        `${p.size}px`,
            height:       `${p.size}px`,
            borderRadius: '50%',
            background:   isDark
              ? `radial-gradient(circle, rgba(168,133,247,0.9), rgba(79,140,247,0.5))`
              : `radial-gradient(circle, rgba(79,63,245,0.7), rgba(79,140,247,0.4))`,
            boxShadow:    isDark ? `0 0 ${p.size * 2.5}px rgba(168,133,247,0.6)` : 'none',
            ['--p-opacity' as never]: p.opacity,
            ['--px' as never]:        p.px,
            ['--py' as never]:        p.py,
            animation:    `ptnParticleDrift ${p.dur} ease-in-out ${p.delay} infinite`,
            opacity:      p.opacity,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

// ── Decorative energy curves for header ──────────────────────────────────────
function EnergyCurves({ C }: { C: ReturnType<typeof useThemePalette> }) {
  const ps = (anim: string): React.CSSProperties => ({
    animation:  anim,
    willChange: 'stroke-dashoffset, opacity',
  });
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 500 400"
      style={{
        position:      'absolute',
        inset:         '-5%',
        width:         '110%',
        height:        '110%',
        pointerEvents: 'none',
        zIndex:        0,
        overflow:      'visible',
      }}
    >
      <defs>
        <linearGradient id="ptn-g1" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={C.accent}  stopOpacity="0"   />
          <stop offset="30%"  stopColor={C.accent}  stopOpacity="0.9" />
          <stop offset="60%"  stopColor={C.accent2} stopOpacity="1"   />
          <stop offset="100%" stopColor={PURPLE}     stopOpacity="0"   />
        </linearGradient>
        <linearGradient id="ptn-g2" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%"   stopColor={PURPLE}     stopOpacity="0"   />
          <stop offset="40%"  stopColor={C.accent3}  stopOpacity="0.8" />
          <stop offset="70%"  stopColor={C.accent2}  stopOpacity="0.9" />
          <stop offset="100%" stopColor={C.accent}   stopOpacity="0"   />
        </linearGradient>
        <linearGradient id="ptn-g3" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor={C.accent2}  stopOpacity="0"   />
          <stop offset="50%"  stopColor={C.accent3}  stopOpacity="0.7" />
          <stop offset="100%" stopColor={PURPLE}      stopOpacity="0"   />
        </linearGradient>
      </defs>

      {/* Curve 1 — soft glow + bright core */}
      <path d="M -20 320 C 80 80, 200 360, 310 180 S 440 20, 520 240"
        fill="none" stroke="url(#ptn-g1)" strokeWidth="16" strokeLinecap="round"
        strokeDasharray="160 9999" opacity="0.26"
        style={ps('ptnEcPulse1 9s ease-in-out infinite')} />
      <path d="M -20 320 C 80 80, 200 360, 310 180 S 440 20, 520 240"
        fill="none" stroke="url(#ptn-g1)" strokeWidth="2" strokeLinecap="round"
        strokeDasharray="160 9999"
        style={ps('ptnEcPulse1 9s ease-in-out infinite')} />

      {/* Curve 2 */}
      <path d="M 520 40 C 400 220, 260 -20, 160 160 S 30 360, -20 140"
        fill="none" stroke="url(#ptn-g2)" strokeWidth="12" strokeLinecap="round"
        strokeDasharray="120 9999" opacity="0.22"
        style={ps('ptnEcPulse2 12s ease-in-out 2s infinite')} />
      <path d="M 520 40 C 400 220, 260 -20, 160 160 S 30 360, -20 140"
        fill="none" stroke="url(#ptn-g2)" strokeWidth="1.5" strokeLinecap="round"
        strokeDasharray="120 9999"
        style={ps('ptnEcPulse2 12s ease-in-out 2s infinite')} />

      {/* Curve 3 */}
      <path d="M 0 400 C 120 260, 300 360, 400 200 S 480 60, 520 -20"
        fill="none" stroke="url(#ptn-g3)" strokeWidth="9" strokeLinecap="round"
        strokeDasharray="100 9999" opacity="0.20"
        style={ps('ptnEcPulse3 15s ease-in-out 5s infinite')} />
      <path d="M 0 400 C 120 260, 300 360, 400 200 S 480 60, 520 -20"
        fill="none" stroke="url(#ptn-g3)" strokeWidth="1.2" strokeLinecap="round"
        strokeDasharray="100 9999"
        style={ps('ptnEcPulse3 15s ease-in-out 5s infinite')} />
    </svg>
  );
}

// ── Mesh orb backdrop ─────────────────────────────────────────────────────────
function MeshOrbs({ isDark: _ }: { isDark: boolean }) {
  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      <div style={{
        position: 'absolute', top: '-10%', left: '0%',
        width: '600px', height: '600px', borderRadius: '50%',
        background: `radial-gradient(circle, rgba(79,63,245,0.16) 0%, rgba(79,140,247,0.07) 45%, transparent 70%)`,
        filter: 'blur(44px)', animation: 'ptnMeshFloat1 18s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', top: '20%', right: '-10%',
        width: '480px', height: '480px', borderRadius: '50%',
        background: `radial-gradient(circle, rgba(124,111,247,0.15) 0%, rgba(168,85,247,0.07) 45%, transparent 70%)`,
        filter: 'blur(54px)', animation: 'ptnMeshFloat2 22s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', bottom: '-5%', left: '30%',
        width: '380px', height: '380px', borderRadius: '50%',
        background: `radial-gradient(circle, rgba(79,140,247,0.11) 0%, transparent 70%)`,
        filter: 'blur(62px)', animation: 'ptnMeshFloat3 26s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', top: '10%', right: '28%',
        width: '280px', height: '280px', borderRadius: '50%',
        background: `radial-gradient(circle, rgba(168,85,247,0.09) 0%, transparent 70%)`,
        filter: 'blur(52px)', animation: 'ptnMeshFloat1 14s ease-in-out 3s infinite',
      }} />
    </div>
  );
}

// ── Orbit ring decoration (decorative, header only) ───────────────────────────
function OrbitRings({ C, isDark }: { C: ReturnType<typeof useThemePalette>; isDark: boolean }) {
  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
      {/* Large slow orbit */}
      <div style={{
        position:     'absolute',
        top:          '50%',
        right:        '-120px',
        width:        '540px',
        height:       '540px',
        borderRadius: '50%',
        border:       `1px solid ${isDark ? 'rgba(124,111,247,0.07)' : 'rgba(79,63,245,0.05)'}`,
        transform:    'translateY(-50%)',
        animation:    'ptnOrbitRing 50s linear infinite',
        willChange:   'transform',
      }}>
        <div style={{
          position:     'absolute',
          top:          '-4px',
          left:         '50%',
          transform:    'translateX(-50%)',
          width:        '8px',
          height:       '8px',
          borderRadius: '50%',
          background:   C.accent2,
          boxShadow:    `0 0 14px 3px ${C.accent2}`,
        }} />
      </div>
      {/* Inner dashed counter-orbit */}
      <div style={{
        position:     'absolute',
        top:          '50%',
        right:        '-30px',
        width:        '360px',
        height:       '360px',
        borderRadius: '50%',
        border:       `1px dashed ${isDark ? 'rgba(124,111,247,0.09)' : 'rgba(79,63,245,0.06)'}`,
        transform:    'translateY(-50%)',
        animation:    'ptnOrbitRingRev 32s linear infinite',
        willChange:   'transform',
      }}>
        <div style={{
          position:     'absolute',
          bottom:       '-3px',
          left:         '50%',
          transform:    'translateX(-50%)',
          width:        '6px',
          height:       '6px',
          borderRadius: '50%',
          background:   C.accent3,
          boxShadow:    `0 0 10px 2px ${C.accent3}`,
          opacity:      0.75,
        }} />
      </div>
    </div>
  );
}

// ── Premium glass stat card (floats in header) ────────────────────────────────
function GlassStatCard({
  num, label, pos, delay, C, isDark,
}: {
  num: string; label: string;
  pos: React.CSSProperties; delay: string;
  C: ReturnType<typeof useThemePalette>; isDark: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position:             'absolute',
        ...pos,
        padding:              '12px 16px',
        borderRadius:         '16px',
        background:           isDark
          ? 'rgba(13,18,32,0.72)'
          : 'rgba(255,255,255,0.84)',
        backdropFilter:       'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border:               `1px solid ${hover
          ? (isDark ? 'rgba(124,111,247,0.45)' : 'rgba(79,63,245,0.30)')
          : (isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)')}`,
        boxShadow:            hover
          ? `0 0 0 1px rgba(124,111,247,0.18), 0 14px 36px rgba(79,63,245,0.28)`
          : `0 4px 22px rgba(0,0,0,0.28)`,
        zIndex:               3,
        minWidth:             '112px',
        animation:            `ptnStatEnter 0.65s ease ${delay} both, ptnStatFloat ${5.2 + parseFloat(delay) * 0.6}s ease-in-out ${delay} infinite`,
        transition:           'border-color 0.22s, box-shadow 0.22s',
        cursor:               'default',
      }}
    >
      <div style={{
        fontSize:             '1.32rem',
        fontWeight:           900,
        letterSpacing:        '-0.02em',
        background:           `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor:  'transparent',
        backgroundClip:       'text',
        lineHeight:           1.15,
      }}>
        {num}
      </div>
      <div style={{
        fontSize:      '0.63rem',
        fontWeight:    700,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color:         C.muted,
        marginTop:     '3px',
      }}>
        {label}
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PartnersPage() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const [width,          setWidth]    = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [hoveredPartner, setHovered]  = useState<string | null>(null);
  const [hoveredStat,    setHovStat]  = useState<number | null>(null);
  const [ctaHover,       setCtaHover] = useState(false);

  // Scroll-reveal state
  const [inView, setInView] = useState({ stats: false, cards: false, cta: false });
  const statsRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const ctaRef   = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle, { passive: true });
    return () => window.removeEventListener('resize', handle);
  }, []);

  // IntersectionObserver scroll reveals
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const observe = (el: Element | null, key: keyof typeof inView) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) { setInView(v => ({ ...v, [key]: true })); obs.disconnect(); } },
        { threshold: 0.10 },
      );
      obs.observe(el);
      observers.push(obs);
    };
    observe(statsRef.current, 'stats');
    observe(cardsRef.current, 'cards');
    observe(ctaRef.current,   'cta');
    return () => observers.forEach(o => o.disconnect());
  }, []);

  const isMobile = width < 768;

  const revealStyle = (visible: boolean, delayMs = 0): React.CSSProperties => ({
    opacity:    visible ? 1 : 0,
    transform:  visible ? 'translateY(0)' : 'translateY(20px)',
    transition: `opacity 0.72s ease ${delayMs}ms, transform 0.72s ease ${delayMs}ms`,
  });

  const enterAnim = (delayMs: number): React.CSSProperties => ({
    animation:      'ptnEnter 0.62s ease both',
    animationDelay: `${delayMs}ms`,
  });

  const sectionStyle: React.CSSProperties = {
    maxWidth: '1280px',
    margin:   '0 auto',
    padding:  isMobile ? '60px 20px' : '80px 40px',
  };

  const stats = [
    { num: '2',   label: 'Partner Countries'   },
    { num: '2+',  label: 'Institutions'        },
    { num: '30+', label: 'Experts Involved'    },
    { num: '2+',  label: 'Years Collaboration' },
  ];

  // Ticker fade masks
  const fadeL = isDark ? 'linear-gradient(to right, #080c14, transparent)' : 'linear-gradient(to right, #f8fafc, transparent)';
  const fadeR = isDark ? 'linear-gradient(to left,  #080c14, transparent)' : 'linear-gradient(to left,  #f8fafc, transparent)';

  // ── Icon helpers ───────────────────────────────────────────────────────────
  const IcoLink     = () => <svg width="13" height="13" viewBox="0 0 24 24" {...SI}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>;
  const IcoUsers    = () => <svg width="13" height="13" viewBox="0 0 24 24" {...SI}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
  const IcoBuilding = () => <svg width="13" height="13" viewBox="0 0 24 24" {...SI}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>;

  // Float stat cards for header
  const headerStats = [
    { num: '2',   label: 'Countries',    pos: { top: '12%',   left: isMobile ? '2%'  : '2%'  } as React.CSSProperties, delay: '0.2s' },
    { num: '30+', label: 'Experts',      pos: { top: '12%',   right: isMobile ? '2%' : '2%'  } as React.CSSProperties, delay: '0.6s' },
    { num: '2+',  label: 'Institutions', pos: { bottom: '12%', left: isMobile ? '2%' : '2%'  } as React.CSSProperties, delay: '1.0s' },
    { num: '2+',  label: 'Years Active', pos: { bottom: '12%', right: isMobile ? '2%': '2%'  } as React.CSSProperties, delay: '1.4s' },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight:  '100vh',
      background: C.bg,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      transition: 'background 0.25s ease, color 0.25s ease',
      color:      C.text,
    }}>

      {/* ════════════════════════ PAGE HEADER ════════════════════════════ */}
      <div style={{
        background:    isDark
          ? 'linear-gradient(160deg, rgba(8,12,20,1) 0%, rgba(14,22,50,1) 100%)'
          : 'linear-gradient(160deg, rgba(248,250,252,1) 0%, rgba(233,241,255,1) 100%)',
        borderBottom:  `1px solid ${C.border}`,
        padding:       isMobile ? '64px 20px 72px' : '88px 40px 96px',
        textAlign:     'center',
        position:      'relative',
        overflow:      'hidden',
        minHeight:     isMobile ? 'auto' : '420px',
        display:       'flex',
        alignItems:    'center',
        justifyContent:'center',
      }}>

        {/* Mesh orbs */}
        <MeshOrbs isDark={isDark} />

        {/* Particles */}
        <ParticleField isDark={isDark} />

        {/* Orbit rings (desktop) */}
        {!isMobile && <OrbitRings C={C} isDark={isDark} />}

        {/* Energy curves */}
        <EnergyCurves C={C} />

        {/* Floating glass stat cards (desktop) */}
        {!isMobile && headerStats.map(s => (
          <GlassStatCard
            key={s.label}
            num={s.num}
            label={s.label}
            pos={s.pos}
            delay={s.delay}
            C={C}
            isDark={isDark}
          />
        ))}

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 2, maxWidth: '680px', margin: '0 auto' }}>

          {/* Eyebrow badge */}
          <div style={{
            display:       'inline-flex',
            alignItems:    'center',
            gap:           '7px',
            background:    `linear-gradient(135deg, rgba(79,63,245,0.16), rgba(124,111,247,0.11))`,
            color:         C.accent2,
            border:        '1px solid rgba(124,111,247,0.35)',
            borderRadius:  '999px',
            padding:       '7px 16px',
            fontSize:      '0.72rem',
            fontWeight:    700,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            marginBottom:  '22px',
            boxShadow:     isDark ? '0 0 18px rgba(124,111,247,0.14)' : 'none',
            ...enterAnim(80),
          }}>
            <IcoLink />
            Strategic Partnerships
          </div>

          {/* Heading */}
          <h1 style={{
            fontSize:      isMobile ? 'clamp(2.2rem, 9vw, 3rem)' : 'clamp(2.8rem, 4.5vw, 4rem)',
            fontWeight:    900,
            lineHeight:    1.08,
            margin:        '0 0 20px',
            letterSpacing: '-0.028em',
            color:         C.text,
            ...enterAnim(200),
          }}>
            Our Strategic{' '}
            <span style={{
              background:           `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 45%, ${PURPLE} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor:  'transparent',
              backgroundClip:       'text',
              filter:               isDark ? 'drop-shadow(0 0 22px rgba(124,111,247,0.5))' : 'none',
            }}>
              Partners
            </span>
          </h1>

          {/* Subtitle */}
          <p style={{
            fontSize:   '1.08rem',
            color:      C.muted,
            maxWidth:   '580px',
            margin:     '0 auto',
            lineHeight: 1.8,
            ...enterAnim(340),
          }}>
            Collaborating with leading sports education institutions to advance ESP learning worldwide
          </p>
        </div>
      </div>

      {/* ════════════════ SECTION 1: Stats + Ticker ══════════════════════ */}
      <section style={sectionStyle}>

        {/* Section eyebrow */}
        <div style={{
          display:       'inline-flex',
          alignItems:    'center',
          gap:           '7px',
          background:    `linear-gradient(135deg, rgba(79,63,245,0.14), rgba(124,111,247,0.09))`,
          color:         C.accent2,
          border:        '1px solid rgba(124,111,247,0.30)',
          borderRadius:  '999px',
          padding:       '6px 15px',
          fontSize:      '0.72rem',
          fontWeight:    700,
          letterSpacing: '0.10em',
          textTransform: 'uppercase',
          marginBottom:  '14px',
          boxShadow:     isDark ? '0 0 16px rgba(124,111,247,0.10)' : 'none',
        }}>
          <IcoUsers />
          Built on Collaboration
        </div>

        <h2 style={{
          fontSize:      'clamp(1.85rem, 4vw, 2.6rem)',
          fontWeight:    900,
          lineHeight:    1.18,
          margin:        '0 0 14px',
          letterSpacing: '-0.022em',
          color:         C.text,
        }}>
          Built on{' '}
          <span style={{
            background:           `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor:  'transparent',
            backgroundClip:       'text',
          }}>
            Collaboration
          </span>
        </h2>

        <p style={{
          fontSize:     '1.05rem',
          color:        C.muted,
          maxWidth:     '640px',
          lineHeight:   1.78,
          margin:       '0 0 44px',
        }}>
          We work with prestigious institutions to develop and deliver high-quality English-for-Specific-Purposes resources for sports professionals worldwide.
        </p>

        {/* ── Stats strip ─────────────────────────────────────────────── */}
        <div
          ref={statsRef}
          style={{
            display:             'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap:                 '16px',
            marginBottom:        '56px',
            ...revealStyle(inView.stats),
          }}
        >
          {stats.map((s, i) => {
            const hov = hoveredStat === i;
            return (
              <div
                key={i}
                onMouseEnter={() => setHovStat(i)}
                onMouseLeave={() => setHovStat(null)}
                style={{
                  textAlign:            'center',
                  background:           isDark
                    ? `rgba(13,18,32,${hov ? '0.80' : '0.55'})`
                    : `rgba(255,255,255,${hov ? '0.95' : '0.80'})`,
                  backdropFilter:       'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  border:               `1px solid ${hov
                    ? (isDark ? 'rgba(124,111,247,0.40)' : 'rgba(79,63,245,0.25)')
                    : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)')}`,
                  borderRadius:         '20px',
                  padding:              '32px 16px',
                  transition:           'transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease, background 0.24s ease',
                  transform:            hov ? 'translateY(-6px)' : 'translateY(0)',
                  boxShadow:            hov
                    ? `0 0 0 1px rgba(124,111,247,0.15), 0 18px 44px rgba(79,63,245,${isDark ? '0.22' : '0.10'})`
                    : (isDark ? '0 4px 20px rgba(0,0,0,0.30)' : '0 2px 12px rgba(0,0,0,0.05)'),
                  cursor:               'default',
                  position:             'relative',
                  overflow:             'hidden',
                }}
              >
                {/* Subtle inner glow on hover */}
                {hov && (
                  <div style={{
                    position:   'absolute',
                    inset:      0,
                    background: `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(124,111,247,${isDark ? '0.12' : '0.06'}) 0%, transparent 100%)`,
                    pointerEvents: 'none',
                    borderRadius:  '20px',
                  }} />
                )}
                <div style={{
                  fontSize:             '2.8rem',
                  fontWeight:           900,
                  lineHeight:           1,
                  marginBottom:         '8px',
                  background:           `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor:  'transparent',
                  backgroundClip:       'text',
                  filter:               hov && isDark ? `drop-shadow(0 0 14px rgba(124,111,247,0.55))` : 'none',
                  position:             'relative',
                }}>
                  {s.num}
                </div>
                <div style={{
                  fontSize:      '0.82rem',
                  color:         C.muted,
                  fontWeight:    600,
                  letterSpacing: '0.02em',
                  position:      'relative',
                }}>
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Logo Ticker ──────────────────────────────────────────────── */}
        <div
          aria-label="Partner logos"
          style={{ position: 'relative', overflow: 'hidden', padding: '18px 0' }}
        >
          {/* Fade masks */}
          <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '120px', background: fadeL, zIndex: 2, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '120px', background: fadeR, zIndex: 2, pointerEvents: 'none' }} />

          <div className="esp-ticker-track" style={{ display: 'flex', gap: '20px', width: 'max-content' }}>
            {TICKER_PARTNERS.map((p, i) => (
              <div
                key={i}
                style={{
                  flexShrink:           0,
                  width:                '210px',
                  height:               '96px',
                  background:           isDark
                    ? 'rgba(13,18,32,0.60)'
                    : 'rgba(255,255,255,0.78)',
                  backdropFilter:       'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border:               `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
                  borderRadius:         '16px',
                  display:              'flex',
                  flexDirection:        'column',
                  alignItems:           'center',
                  justifyContent:       'center',
                  gap:                  '8px',
                  padding:              '14px',
                  overflow:             'hidden',
                  boxShadow:            isDark ? '0 4px 18px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.05)',
                }}
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  style={{ maxHeight: '50px', maxWidth: '146px', objectFit: 'contain', display: 'block' }}
                />
                <span style={{
                  fontSize:      '0.62rem',
                  fontWeight:    700,
                  color:         C.muted,
                  letterSpacing: '0.10em',
                  textTransform: 'uppercase',
                }}>
                  {p.countryCode}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ SECTION 2: Partner Cards ═══════════════════════ */}
      <section style={{ ...sectionStyle, paddingTop: 0 }}>

        {/* Section eyebrow */}
        <div style={{
          display:       'inline-flex',
          alignItems:    'center',
          gap:           '7px',
          background:    `linear-gradient(135deg, rgba(79,63,245,0.14), rgba(124,111,247,0.09))`,
          color:         C.accent2,
          border:        '1px solid rgba(124,111,247,0.30)',
          borderRadius:  '999px',
          padding:       '6px 15px',
          fontSize:      '0.72rem',
          fontWeight:    700,
          letterSpacing: '0.10em',
          textTransform: 'uppercase',
          marginBottom:  '14px',
          boxShadow:     isDark ? '0 0 16px rgba(124,111,247,0.10)' : 'none',
        }}>
          <IcoBuilding />
          Our Partners
        </div>

        <h2 style={{
          fontSize:      'clamp(1.85rem, 4vw, 2.6rem)',
          fontWeight:    900,
          lineHeight:    1.18,
          margin:        '0 0 14px',
          letterSpacing: '-0.022em',
          color:         C.text,
        }}>
          Meet Our{' '}
          <span style={{
            background:           `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor:  'transparent',
            backgroundClip:       'text',
          }}>
            Institutions
          </span>
        </h2>

        <p style={{
          fontSize:   '1.05rem',
          color:      C.muted,
          maxWidth:   '640px',
          lineHeight: 1.78,
          margin:     '0 0 36px',
        }}>
          Each partner brings unique expertise that enriches our curriculum and broadens our global reach.
        </p>

        {/* ── Partner cards grid ───────────────────────────────────────── */}
        <div
          ref={cardsRef}
          style={{
            display:             'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap:                 '24px',
            marginBottom:        '44px',
          }}
        >
          {PARTNERS.map((p, idx) => {
            const hov = hoveredPartner === p.id;
            return (
              <div
                key={p.id}
                onMouseEnter={() => setHovered(p.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  background:           isDark
                    ? `rgba(13,18,32,${hov ? '0.82' : '0.58'})`
                    : `rgba(255,255,255,${hov ? '0.96' : '0.80'})`,
                  backdropFilter:       'blur(18px)',
                  WebkitBackdropFilter: 'blur(18px)',
                  border:               `1px solid ${hov
                    ? (isDark ? 'rgba(124,111,247,0.42)' : 'rgba(79,63,245,0.28)')
                    : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)')}`,
                  borderRadius:         '24px',
                  padding:              isMobile ? '28px 24px' : '38px',
                  transition:           'transform 0.26s ease, box-shadow 0.26s ease, border-color 0.26s ease, background 0.26s ease',
                  transform:            hov ? 'translateY(-7px)' : 'translateY(0)',
                  boxShadow:            hov
                    ? `0 0 0 1px rgba(124,111,247,0.18), 0 22px 56px rgba(79,63,245,${isDark ? '0.25' : '0.12'})`
                    : (isDark ? '0 4px 24px rgba(0,0,0,0.32)' : '0 2px 14px rgba(0,0,0,0.06)'),
                  position:             'relative',
                  overflow:             'hidden',
                  cursor:               'default',
                  // Staggered reveal
                  opacity:              inView.cards ? 1 : 0,
                  animation:            inView.cards ? `ptnCardReveal 0.62s ease ${idx * 0.14}s both` : 'none',
                }}
              >
                {/* Inner radial glow on hover */}
                <div style={{
                  position:      'absolute',
                  inset:         0,
                  background:    `radial-gradient(ellipse 75% 55% at 50% 0%, rgba(124,111,247,${hov ? (isDark ? '0.14' : '0.06') : '0'}) 0%, transparent 100%)`,
                  pointerEvents: 'none',
                  borderRadius:  '24px',
                  transition:    'opacity 0.26s ease',
                }} />

                {/* Animated bottom accent bar */}
                <div style={{
                  position:        'absolute',
                  bottom:          0,
                  left:            0,
                  right:           0,
                  height:          '3px',
                  background:      `linear-gradient(90deg, ${C.accent}, ${C.accent2}, ${PURPLE})`,
                  transform:       `scaleX(${hov ? 1 : 0})`,
                  transition:      'transform 0.28s ease',
                  transformOrigin: 'left',
                  borderRadius:    '0 0 0 0',
                }} />

                {/* Logo container */}
                <div style={{
                  width:                '78px',
                  height:               '78px',
                  borderRadius:         '18px',
                  background:           isDark
                    ? 'rgba(255,255,255,0.07)'
                    : 'rgba(0,0,0,0.04)',
                  border:               `1px solid ${hov
                    ? (isDark ? 'rgba(124,111,247,0.30)' : 'rgba(79,63,245,0.18)')
                    : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')}`,
                  display:              'flex',
                  alignItems:           'center',
                  justifyContent:       'center',
                  marginBottom:         '22px',
                  overflow:             'hidden',
                  padding:              '10px',
                  backdropFilter:       'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  boxShadow:            hov
                    ? `0 0 0 1px rgba(124,111,247,0.14), 0 6px 18px rgba(79,63,245,${isDark ? '0.20' : '0.08'})`
                    : 'none',
                  transition:           'border-color 0.22s, box-shadow 0.22s',
                  position:             'relative',
                }}>
                  <img src={p.logo} alt={p.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
                </div>

                {/* Tags */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', position: 'relative' }}>
                  <span style={{
                    padding:       '4px 12px',
                    borderRadius:  '999px',
                    fontSize:      '0.70rem',
                    fontWeight:    700,
                    background:    `linear-gradient(135deg, rgba(79,63,245,0.16), rgba(124,111,247,0.10))`,
                    color:         C.accent2,
                    border:        '1px solid rgba(124,111,247,0.28)',
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                  }}>
                    {p.tag}
                  </span>
                  <span style={{
                    padding:      '4px 12px',
                    borderRadius: '999px',
                    fontSize:     '0.70rem',
                    fontWeight:   600,
                    background:   isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    color:        C.muted,
                    border:       `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
                  }}>
                    {p.country}
                  </span>
                </div>

                <h3 style={{
                  fontSize:      '1.18rem',
                  fontWeight:    800,
                  color:         C.text,
                  margin:        '0 0 14px',
                  lineHeight:    1.32,
                  letterSpacing: '-0.01em',
                  position:      'relative',
                }}>
                  {p.name}
                </h3>

                <p style={{
                  color:      C.muted,
                  lineHeight: 1.75,
                  fontSize:   '0.96rem',
                  margin:     '0 0 24px',
                  position:   'relative',
                }}>
                  {p.description}
                </p>

                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display:        'inline-flex',
                    alignItems:     'center',
                    gap:            '6px',
                    color:          C.accent2,
                    textDecoration: 'none',
                    fontWeight:     700,
                    fontSize:       '0.92rem',
                    transition:     'gap 0.2s ease, filter 0.2s ease',
                    position:       'relative',
                    letterSpacing:  '0.01em',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.gap = '10px';
                    (e.currentTarget as HTMLElement).style.filter = isDark ? 'brightness(1.15)' : 'brightness(0.88)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.gap = '6px';
                    (e.currentTarget as HTMLElement).style.filter = 'none';
                  }}
                >
                  Visit Website
                  <svg width="14" height="14" viewBox="0 0 24 24" {...SI}>
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              </div>
            );
          })}
        </div>

        {/* ── Become a Partner CTA ─────────────────────────────────────── */}
        <div
          ref={ctaRef}
          style={{
            borderRadius: '28px',
            padding:      isMobile ? '52px 28px' : '68px 60px',
            textAlign:    'center',
            color:        '#fff',
            position:     'relative',
            overflow:     'hidden',
            background:   `linear-gradient(135deg, #001f4d 0%, #003380 35%, ${C.accent} 75%, ${PURPLE} 100%)`,
            boxShadow:    `0 24px 72px rgba(79,63,245,${isDark ? '0.35' : '0.22'}), 0 0 0 1px rgba(124,111,247,0.20)`,
            ...revealStyle(inView.cta, 80),
          }}
        >
          {/* Mesh orbs inside CTA */}
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', borderRadius: '28px' }}>
            <div style={{
              position: 'absolute', top: '-30%', left: '-10%',
              width: '500px', height: '500px', borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.09) 0%, transparent 65%)',
              animation: 'ptnMeshFloat1 18s ease-in-out infinite',
            }} />
            <div style={{
              position: 'absolute', bottom: '-20%', right: '-5%',
              width: '380px', height: '380px', borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 65%)',
              animation: 'ptnMeshFloat2 22s ease-in-out infinite',
            }} />
          </div>

          {/* Particle field */}
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', borderRadius: '28px' }}>
            {Array.from({ length: 14 }, (_, i) => ({
              left:    `${10 + i * 6.5}%`,
              top:     `${15 + (i % 3) * 30}%`,
              size:    1.2 + (i % 3) * 0.6,
              opacity: 0.12 + (i % 4) * 0.06,
              dur:     `${10 + i * 1.5}s`,
              delay:   `${i * 0.8}s`,
            })).map((p, i) => (
              <div key={i} style={{
                position:     'absolute',
                left:         p.left,
                top:          p.top,
                width:        `${p.size}px`,
                height:       `${p.size}px`,
                borderRadius: '50%',
                background:   'rgba(255,255,255,0.9)',
                opacity:      p.opacity,
                animation:    `ptnParticleDrift ${p.dur} ease-in-out ${p.delay} infinite`,
                ['--p-opacity' as never]: p.opacity,
                ['--px' as never]:        '20px',
                ['--py' as never]:        '-15px',
              } as React.CSSProperties} />
            ))}
          </div>

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 2 }}>
            {/* Icon circle */}
            <div style={{
              width:          '56px',
              height:         '56px',
              borderRadius:   '50%',
              background:     'rgba(255,255,255,0.14)',
              border:         '1px solid rgba(255,255,255,0.30)',
              backdropFilter: 'blur(10px)',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              margin:         '0 auto 24px',
              boxShadow:      '0 0 24px rgba(255,255,255,0.12)',
              color:          '#fff',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>

            <h3 style={{
              fontSize:      isMobile ? '1.65rem' : '2rem',
              fontWeight:    900,
              margin:        '0 0 18px',
              letterSpacing: '-0.02em',
              lineHeight:    1.15,
            }}>
              Become a Partner
            </h3>

            <p style={{
              opacity:    0.90,
              maxWidth:   '680px',
              margin:     '0 auto 14px',
              lineHeight: 1.78,
              fontSize:   '1.02rem',
            }}>
              START collaborates with prestigious sports education institutions worldwide to develop high-quality ESP resources. Through strategic partnerships we combine linguistic expertise with sports science knowledge to create comprehensive, practically applicable learning materials.
            </p>

            <p style={{
              opacity:    0.80,
              maxWidth:   '600px',
              margin:     '0 auto 36px',
              lineHeight: 1.72,
              fontSize:   '0.97rem',
            }}>
              Interested in joining our network? We welcome universities, sports federations, and language training organizations.
            </p>

            <a
              href="/contact"
              onMouseEnter={() => setCtaHover(true)}
              onMouseLeave={() => setCtaHover(false)}
              style={{
                display:              'inline-flex',
                alignItems:           'center',
                gap:                  '9px',
                background:           ctaHover ? 'rgba(255,255,255,0.24)' : 'rgba(255,255,255,0.14)',
                color:                '#fff',
                border:               '1px solid rgba(255,255,255,0.38)',
                backdropFilter:       'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                padding:              '14px 30px',
                borderRadius:         '999px',
                textDecoration:       'none',
                fontWeight:           700,
                fontSize:             '0.97rem',
                transition:           'background 0.22s ease, transform 0.22s ease, box-shadow 0.22s ease',
                transform:            ctaHover ? 'translateY(-4px)' : 'translateY(0)',
                boxShadow:            ctaHover
                  ? '0 14px 38px rgba(0,0,0,0.22), 0 0 0 2px rgba(255,255,255,0.14)'
                  : '0 4px 18px rgba(0,0,0,0.15)',
                letterSpacing:        '0.01em',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}